import type { StreetOrientationName } from "building-common/streetOrientation/streetOrientationTools";
import type { Building } from "building-types/building/buildingTypes";
import type { OpeningTrimType } from "building-types/casing/openingTrimTypes";
import type { OpeningTrimOpeningType } from "building-types/casing/openingTrimTypes";
import type { Doc } from "building-types/doc";
import type { DocEntityHandle } from "building-types/handles/docEntityHandleTypes";
import type { PaletteMaterialSlot } from "building-types/materials/paletteMaterialSlotTypes";
import type { WorldInches } from "common/types/nominalNumbersTypes";
import type { Plane } from "geom/planeTypes";
import type { PolyLine3 } from "geom/polyLine3";

/**
 * The subset of `Building` the exterior-trim collectors read. Narrowed (rather than the whole
 * `Building`) so the EXTERIOR_TRIM controller's `select` projects only what it needs. A full
 * `Building` is assignable to this, so existing callers are unaffected.
 */
export type ExteriorTrimCollectionBuilding = Pick<
  Building,
  | "buildingGraph"
  | "cornerBoards"
  | "interiorWallPlaneTrimMoldings"
  | "levels"
  | "planarGraphPerimeters"
  | "porches"
  | "roofSystems"
  | "settings"
  | "walls"
  | "wallPlaneTrims"
  | "wallSweeps"
  | "enabledFeatures"
> & {
  readonly doc: Pick<Doc, "rooms" | "profiles">;
};

export enum TrimKeyMeasureGroup {
  // window and door interior trim
  WINDOW_TRIM = "WINDOW_TRIM",
  DOOR_TRIM = "DOOR_TRIM",
  // window and door exterior trim
  WINDOW_CASING = "WINDOW_CASING",
  DOOR_CASING = "DOOR_CASING",
  FACADE_TRIM = "FACADE_TRIM",
  ROOF_TRIM = "ROOF_TRIM",
  PERIMETER = "PERIMETER",
}

export type TrimKeyMeasureType =
  | "CORNER_BOARDS"
  | "INSIDE_CORNER_BOARDS"
  | "FASCIA_BOARDS"
  | "FASCIA_TOP_TRIMS"
  | "FRIEZE_BOARDS"
  | "BELLY_BANDS"
  | "KEYSTONE"
  | "PORCH_HEADER"
  | "ROOM_FACADE_TRIMS"
  | "ROOF_FACADE_TRIMS"
  | `${OpeningTrimOpeningType}_${OpeningTrimType}`;

export enum TrimKeyMeasureCondition {
  INTERIOR = "INTERIOR",
  EXTERIOR = "EXTERIOR",
}

export interface TrimKeyMeasure {
  readonly condition?: TrimKeyMeasureCondition;
  readonly category: TrimKeyMeasureType;
  readonly name: string;
  readonly length: WorldInches;
  readonly profileId?: string;
  readonly height?: WorldInches;
  readonly width?: WorldInches;
  readonly materialSlot?: PaletteMaterialSlot;
  /**
   * some trim is measured in units
   */
  readonly count?: number;
  readonly rail?: PolyLine3;
  /**
   * This it optional because Procure V1 does not support handles.
   * @todo(Andrew): Remove the question mark once Procure V1 is deprecated.
   */
  readonly handles?: DocEntityHandle[];
  readonly plane?: Plane;
  readonly streetOrientation?: StreetOrientationName;
}
